import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import fetchQuestionImageUrls from '../utils/questionAssets';
import useQuestionDetailStore from '../store/questionDetailStore';

const useQuestionDetailState = (question) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    imageUrls, htmlUrls, assetsLoading, assetsError,
    questionText, answerText, isActive, monthYearInput,
    newImages, deletedImages, newHtmlFile, deletedHtmlFiles,
    saveLoading, saveError, saveSuccess,
    showHtmlModal, htmlViewContent,
    questionId, moduleTopicUuid, questionUuid,
    initQuestion, resetStore,
    setImageUrls, setHtmlUrls, setAssetsLoading, setAssetsError,
    setQuestionText, setAnswerText, setIsActive, setMonthYearInput,
    setNewImages, addNewImages, removeNewImage,
    addDeletedImage, setDeletedImages,
    setNewHtmlFile, addDeletedHtmlFile, setDeletedHtmlFiles,
    setSaveLoading, setSaveError,
    setShowHtmlModal, setHtmlViewContent,
    restoreFiles,
    zoomImage, setZoomImage
  } = useQuestionDetailStore();

  useEffect(() => {
    restoreFiles();
  }, [restoreFiles]);

  useEffect(() => {
    const isNewNav = location.state?.isNewNavigation;

    if (isNewNav) {
      resetStore();
      if (question) {
        initQuestion(question);
      }
      navigate(location.pathname, { state: { ...location.state, isNewNavigation: false }, replace: true });
    } else if (question && question.id !== questionId) {
      initQuestion(question);
    }
  }, [question, questionId, initQuestion, resetStore, location.state, location.pathname, navigate]);


  const loadAssets = useCallback(async () => {
    if (question && question.id !== questionId) return;
    if (!moduleTopicUuid || !questionUuid) return;
    try {
      setAssetsLoading(true);
      setAssetsError(null);
      const assets = await fetchQuestionImageUrls(moduleTopicUuid, questionUuid);
      setImageUrls(assets?.imageUrls || []);
      setHtmlUrls(assets?.htmlUrls || []);
    } catch (err) {
      console.error('Failed to load question assets', { err, moduleTopicUuid, questionUuid });
      setAssetsError('Unable to load associated images.');
    } finally {
      setAssetsLoading(false);
    }
  }, [question, questionId, moduleTopicUuid, questionUuid, setAssetsLoading, setAssetsError, setImageUrls, setHtmlUrls]);

  const handleFileChange = useCallback((e) => {
    addNewImages(Array.from(e.target.files || []));
  }, [addNewImages]);

  const handleRemoveNewImage = useCallback((index) => {
    removeNewImage(index);
  }, [removeNewImage]);

  const handleImageDelete = useCallback((imageName) => {
    addDeletedImage(imageName);
    setImageUrls(imageUrls.filter(img => img.fileName !== imageName));
  }, [addDeletedImage, setImageUrls, imageUrls]);

  const handleHtmlFileChange = useCallback((file) => {
    setNewHtmlFile(file);
  }, [setNewHtmlFile]);

  const handleRemoveNewHtml = useCallback(() => {
    setNewHtmlFile(null);
  }, [setNewHtmlFile]);

  const handleHtmlDelete = useCallback((fileName) => {
    addDeletedHtmlFile(fileName);
    setHtmlUrls(htmlUrls.filter((html) => html.fileName !== fileName));
  }, [addDeletedHtmlFile, setHtmlUrls, htmlUrls]);

  const handleSave = useCallback(async (overrides = {}) => {
    if (!questionId) return;
    setSaveError(null);
    const formData = new FormData();
    formData.append('id', questionId);
    if (questionUuid) formData.append('uuid', questionUuid);
    formData.append('questionText', questionText);
    formData.append('text', questionText);
    formData.append('answerText', answerText);
    const activeState = overrides.hasOwnProperty('isActive') ? overrides.isActive : isActive;
    formData.append('isActive', activeState ? 'true' : 'false');

    const entries = monthYearInput.split(',').map(e => e.trim());
    entries.forEach(entry => {
      const parts = entry.split(/\s+/);
      if (parts.length === 2) {
        const [month, year] = parts;
        if (/^[A-Za-z]+$/.test(month) && /^\d{4}$/.test(year)) {
          formData.append('month', month);
          formData.append('year', year);
        }
      }
    });

    newImages.forEach((file) => formData.append('images', file));
    if (newHtmlFile) {
      formData.append('htmlFile', newHtmlFile);
    }
    deletedImages.forEach((img) => formData.append('imagesToDelete', img));
    deletedHtmlFiles.forEach((file) => formData.append('imagesToDelete', file));

    try {
      setSaveLoading(true);
      await apiClient.put(API_ENDPOINTS.updateQuestionBank, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDeletedHtmlFiles([]);
      setNewImages([]);
      setNewHtmlFile(null);
      setDeletedImages([]);
      toast.success('Question updated successfully.');
      navigate('/question-bank');
      resetStore();
    } catch (err) {
      console.error('Failed to update question', err);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  }, [questionId, questionUuid, questionText, answerText, isActive, monthYearInput, newImages, deletedImages, newHtmlFile, deletedHtmlFiles, setSaveLoading, setSaveError, setDeletedHtmlFiles, setNewImages, setNewHtmlFile, setDeletedImages, navigate, resetStore]);

  const toggleActiveAndSave = useCallback(async () => {
    const newStatus = !isActive;
    setIsActive(newStatus);
    await handleSave({ isActive: newStatus });
  }, [isActive, setIsActive, handleSave]);

  const handleHtmlPreview = useCallback(async (htmlFile) => {
    try {
      const response = await fetch(htmlFile.url);
      const originalHtml = await response.text();
      const updatedHtml = originalHtml.replace(/(<img\s+[^>]*src=["'])([^"']+)(["'])/gi, (match, prefix, src, suffix) => {
        const matchingImage = imageUrls.find(img => img.fileName === src || src.endsWith(img.fileName));
        if (matchingImage) {
          return `${prefix}${matchingImage.url}${suffix}`;
        }
        return match;
      });
      setHtmlViewContent(updatedHtml);
      setShowHtmlModal(true);
    } catch (error) {
      console.error("Failed to load HTML content", error);
    }
  }, [imageUrls, setHtmlViewContent, setShowHtmlModal]);

  return {
    imageUrls,
    htmlUrls,
    assetsLoading,
    assetsError,
    questionText,
    setQuestionText,
    answerText,
    setAnswerText,
    isActive,
    setIsActive,
    monthYearInput,
    setMonthYearInput,
    newImages,
    saveLoading,
    saveError,
    saveSuccess,
    handleFileChange,
    handleSave,
    loadAssets,
    handleImageDelete,
    handleRemoveNewImage,
    newHtmlFile,
    handleHtmlFileChange,
    handleRemoveNewHtml,
    handleHtmlDelete,
    toggleActiveAndSave,
    showHtmlModal,
    setShowHtmlModal,
    htmlViewContent,
    handleHtmlPreview,
    questionId,
    zoomImage,
    setZoomImage
  };
};

export default useQuestionDetailState;
